import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent } from './ui/card';
import ProfileMenu from './ProfileMenu';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const FilesTab = ({ user, isDarkMode, setIsDarkMode, showNotification, searchTicker, onSearchComplete, openFileViewerId, onOpenFileComplete, onOpenGlobalSearch, activeView, onViewChange, onShareFile, showConfirmModal, closeConfirmModal, onFileCreated, userTickers, onLogout, onNavigateToAccount, onNavigateToSchedules, isWorkspaceMode = false, highlightedTicker = '', refreshTrigger = 0 }) => {
  const [dossiers, setDossiers] = useState([]);
  const [todayScheduled, setTodayScheduled] = useState([]);
  const [allScheduledItems, setAllScheduledItems] = useState([]); // Full schedule for "no file" lookup
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showDossierModal, setShowDossierModal] = useState(false);
  const [dossierUrl, setDossierUrl] = useState(null);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [tickerSuggestions, setTickerSuggestions] = useState([]);
  const [filterView, setFilterView] = useState('all'); // 'all' or 'scheduled'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [secAvailability, setSecAvailability] = useState({ loading: false, hasFilings: null, hasForeignFilings: null, message: '' });
  const [selectedDocTypes, setSelectedDocTypes] = useState({
    include10K: true,
    include10Q: true,
    include8K: false,
    include20F: false,
    include6K: false,
    includeProxy: false,
    includeStatements: false
  });
  const [expandedCards, setExpandedCards] = useState({});
  const [cardDocuments, setCardDocuments] = useState({}); // Store documents for each dossier
  const [expandedFilingTypes, setExpandedFilingTypes] = useState({}); // Track which filing types are expanded within each card
  const [targetSectionHash, setTargetSectionHash] = useState(null); // Track target section for iframe navigation
  const [viewerReady, setViewerReady] = useState(false); // Track when viewer iframe is ready
  const [addingToViewerId, setAddingToViewerId] = useState(null); // Track which existing viewer we're adding documents to
  const [addingDocuments, setAddingDocuments] = useState(false); // Loading state for adding documents
  const [notificationShown, setNotificationShown] = useState(false); // Guard against duplicate notifications
  const [showNoFileModal, setShowNoFileModal] = useState(false); // Show "file not created" prompt
  const [noFileTickerInfo, setNoFileTickerInfo] = useState(null); // Info about the ticker with no file
  const iframeRef = useRef(null);
  const cardRefs = useRef({}); // Store refs for each card by viewer_id

  // Use controlled view from parent (for swipe) or local state
  const activeTab = activeView || 'companies';
  const setActiveTab = onViewChange || (() => {});

  // Listen for messages from iframe to close modal or signal ready
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === 'closeDossierModal' || event.data === 'closeFileModal') {
        closeDossierModal();
      } else if (event.data && event.data.type === 'viewerReady') {
        console.log('Viewer signaled ready');
        setViewerReady(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowSortDropdown(false);
      setShowFilterDropdown(false);
    };
    if (showSortDropdown || showFilterDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSortDropdown, showFilterDropdown]);

  const fetchDossiers = useCallback(async () => {
    try {
      setLoading(true);
      
      const viewersRes = await fetch(`${API_BASE_URL}/api/viewers/list?user_id=${encodeURIComponent(user?.id || '')}`);
      if (viewersRes.ok) {
        const data = await viewersRes.json();
        setDossiers(data.viewers || []);
      }

      if (user?.id) {
        // Use local time, not UTC (toISOString uses UTC which can show wrong day)
        const now = new Date();
        const today = now.getFullYear() + '-' + 
          String(now.getMonth() + 1).padStart(2, '0') + '-' + 
          String(now.getDate()).padStart(2, '0');
        const scheduleRes = await fetch(
          `${API_BASE_URL}/api/research-schedule/${user.id}?date=${today}`
        );
        if (scheduleRes.ok) {
          const scheduleData = await scheduleRes.json();
          // API returns 'schedule' not 'items', filter for today's date
          const allSchedule = scheduleData.schedule || [];
          const todayItems = allSchedule.filter(item => item.scheduled_date === today);
          setTodayScheduled(todayItems);
          setAllScheduledItems(allSchedule); // Store full schedule for lookup
          console.log(`📅 Today's scheduled items (${today}):`, todayItems.map(i => i.ticker));
        }
      }
    } catch (error) {
      console.error('Error fetching dossiers:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDossiers();
  }, [fetchDossiers]);

  // Respond to refresh trigger from parent (workspace cross-panel sync)
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('🔵 FilesTab: refreshTrigger changed, refetching dossiers...');
      fetchDossiers();
    }
  }, [refreshTrigger, fetchDossiers]);

  // Handle opening a specific file by viewerId (from folders/search navigation)
  useEffect(() => {
    if (openFileViewerId && dossiers.length > 0) {
      // Find the dossier and expand it
      const dossier = dossiers.find(d => d.viewer_id === openFileViewerId);
      if (dossier) {
        // Expand the card
        setExpandedCards(prev => ({ ...prev, [openFileViewerId]: true }));
        // Fetch documents for this dossier
        fetchDocumentsForDossier(openFileViewerId, dossier.ticker);
        // Scroll to the card after a brief delay
        setTimeout(() => {
          const cardElement = document.querySelector(`[data-testid="dossier-card-${dossier.ticker || openFileViewerId}"]`);
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      // Clear the prop after handling
      onOpenFileComplete?.();
    }
  }, [openFileViewerId, dossiers]);

  useEffect(() => {
    // Wait for dossiers to load before processing searchTicker
    if (searchTicker && !loading) {
      // Check if a file already exists for this ticker
      const existingDossier = dossiers.find(d => 
        d.ticker?.toUpperCase() === searchTicker.toUpperCase()
      );
      
      if (existingDossier) {
        // File exists - expand the card instead of opening viewer
        console.log('🔵 FilesTab: File exists for ticker, expanding card:', searchTicker);
        setExpandedCards(prev => ({ ...prev, [existingDossier.viewer_id]: true }));
        fetchDocumentsForDossier(existingDossier.viewer_id, existingDossier.ticker);
        // Scroll to the card after a brief delay
        setTimeout(() => {
          const cardElement = document.querySelector(`[data-testid="dossier-card-${existingDossier.ticker || existingDossier.viewer_id}"]`);
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        onSearchComplete?.();
      } else {
        // No file exists - show prompt to user instead of auto-creating
        console.log('🔵 FilesTab: No file for ticker, showing prompt:', searchTicker);
        
        // Find scheduled info for this ticker from all scheduled items
        const scheduledItem = allScheduledItems.find(item => 
          item.ticker?.toUpperCase() === searchTicker.toUpperCase()
        );
        
        setNoFileTickerInfo({
          ticker: searchTicker.toUpperCase(),
          scheduledDate: scheduledItem?.scheduled_date || null,
          companyName: scheduledItem?.company_name || null
        });
        setShowNoFileModal(true);
        onSearchComplete?.();
      }
    }
  }, [searchTicker, loading, dossiers, allScheduledItems]);

  // Check SEC availability for a ticker
  const checkSecAvailability = async (ticker) => {
    if (!ticker) {
      setSecAvailability({ loading: false, hasFilings: null, hasForeignFilings: null, message: '' });
      return;
    }
    
    setSecAvailability({ loading: true, hasFilings: null, hasForeignFilings: null, message: 'Checking SEC filings...' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/check-sec-availability/${ticker.toUpperCase()}`);
      const data = await response.json();
      
      // Explicitly check for true values
      const isUsCompany = data.has_filings === true;
      const isForeignFiler = data.has_foreign_filings === true && data.has_filings !== true;
      const noFilings = !isUsCompany && !isForeignFiler;
      
      console.log('📊 SEC Check Result:', { 
        ticker, 
        raw: { has_filings: data.has_filings, has_foreign_filings: data.has_foreign_filings },
        computed: { isUsCompany, isForeignFiler, noFilings }
      });
      
      setSecAvailability({
        loading: false,
        hasFilings: isUsCompany,
        hasForeignFilings: isForeignFiler,
        noFilings: noFilings,
        message: data.message || ''
      });
      
      // Set checkboxes based on company type
      if (isUsCompany) {
        setSelectedDocTypes({
          include10K: true,
          include10Q: true,
          include8K: false,
          include20F: false,
          include6K: false,
          includeProxy: false,
          includeStatements: false
        });
      } else if (isForeignFiler) {
        setSelectedDocTypes({
          include10K: false,
          include10Q: false,
          include8K: false,
          include20F: true,
          include6K: true,
          includeProxy: false,
          includeStatements: false
        });
      } else {
        setSelectedDocTypes({
          include10K: false,
          include10Q: false,
          include8K: false,
          include20F: false,
          include6K: false,
          includeProxy: false,
          includeStatements: false
        });
      }
    } catch (error) {
      setSecAvailability({
        loading: false,
        hasFilings: false,
        hasForeignFilings: false,
        noFilings: true,
        message: 'Error checking SEC filings'
      });
    }
  };

  // Auto-check SEC availability when user stops typing (debounced)
  useEffect(() => {
    if (!newTicker || newTicker.length < 2) return;
    
    // Debounce: wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      // Only auto-check if there are no suggestions showing (user typed a direct ticker)
      // or if the ticker looks like a valid symbol (2-5 uppercase letters)
      const isValidTicker = /^[A-Z]{2,5}$/.test(newTicker);
      if (isValidTicker && tickerSuggestions.length === 0) {
        checkSecAvailability(newTicker);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [newTicker, tickerSuggestions.length]);

  // Search for ticker suggestions from user's saved data (ideas, watchlists, polls)
  const searchTickers = async (query) => {
    if (!query || query.length < 1) {
      setTickerSuggestions([]);
      return;
    }
    
    const searchQuery = query.toUpperCase();
    const tickerMap = new Map();
    
    // Search user's ideas
    userTickers?.ideas?.forEach(item => {
      if (item.ticker?.toUpperCase().includes(searchQuery) || 
          item.company?.toUpperCase().includes(searchQuery)) {
        if (!tickerMap.has(item.ticker)) {
          tickerMap.set(item.ticker, {
            ticker: item.ticker,
            company: item.company || item.ticker,
            source: 'idea'
          });
        }
      }
    });
    
    // Search user's watchlists
    userTickers?.watchlists?.forEach(item => {
      if (item.ticker?.toUpperCase().includes(searchQuery) || 
          item.company?.toUpperCase().includes(searchQuery)) {
        if (!tickerMap.has(item.ticker)) {
          tickerMap.set(item.ticker, {
            ticker: item.ticker,
            company: item.company || item.ticker,
            source: 'watchlist'
          });
        }
      }
    });
    
    // Search user's polls
    userTickers?.polls?.forEach(item => {
      if (item.ticker?.toUpperCase().includes(searchQuery) || 
          item.company?.toUpperCase().includes(searchQuery)) {
        if (!tickerMap.has(item.ticker)) {
          tickerMap.set(item.ticker, {
            ticker: item.ticker,
            company: item.company || item.ticker,
            source: 'poll'
          });
        }
      }
    });
    
    // If we have matches from user data, use those
    if (tickerMap.size > 0) {
      setTickerSuggestions(Array.from(tickerMap.values()).slice(0, 8));
      return;
    }
    
    // Fall back to API search if no user data matches
    try {
      const res = await fetch(`${API_BASE_URL}/api/search?q=${encodeURIComponent(query)}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        data.results?.forEach(r => {
          const ticker = r.tickers?.[0];
          if (ticker && !tickerMap.has(ticker)) {
            tickerMap.set(ticker, {
              ticker: ticker,
              company: r.company_name || r.title?.split(' - ')?.[0] || ticker,
              source: 'search'
            });
          }
        });
        setTickerSuggestions(Array.from(tickerMap.values()).slice(0, 8));
      }
    } catch (error) {
      console.error('Error searching tickers:', error);
    }
  };

  // Toggle card expansion
  const toggleCardExpand = async (viewerId, ticker) => {
    setExpandedCards(prev => {
      const newState = { ...prev, [viewerId]: !prev[viewerId] };
      // If expanding, fetch document info for this dossier
      if (newState[viewerId] && !cardDocuments[viewerId]) {
        fetchDocumentsForDossier(viewerId, ticker);
      }
      return newState;
    });
  };

  // Fetch documents info for a specific dossier from the API
  const fetchDocumentsForDossier = async (viewerId, ticker) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/viewer/${viewerId}/filings`);
      if (response.ok) {
        const data = await response.json();
        setCardDocuments(prev => ({
          ...prev,
          [viewerId]: {
            ticker: data.ticker || ticker,
            filings: data.filings || [],
            sections_count: data.sections_count || 0
          }
        }));
      } else {
        // Fallback to dossier data if API fails
        const dossier = dossiers.find(d => d.viewer_id === viewerId);
        const filingTypes = dossier?.filing_types || ['10-K', '10-Q'];
        const filingNames = {
          '10-K': { name: '10-K Annual Report', description: 'Comprehensive annual financial report' },
          '10-Q': { name: '10-Q Quarterly Report', description: 'Quarterly financial update' },
          '8-K': { name: '8-K Current Report', description: 'Material events and changes' },
          '20-F': { name: '20-F Annual Report', description: 'Foreign company annual report' },
          '6-K': { name: '6-K Report', description: 'Foreign company interim report' },
          'Proxy': { name: 'Proxy Statement', description: 'Executive compensation & governance' }
        };
        setCardDocuments(prev => ({
          ...prev,
          [viewerId]: {
            ticker: ticker || dossier?.ticker,
            filings: filingTypes.map(type => ({
              type,
              name: filingNames[type]?.name || type,
              description: filingNames[type]?.description || 'SEC Filing',
              hash: `${type.toLowerCase()}-group`
            })),
            sections_count: dossier?.sections_count || 0
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching documents for dossier:', error);
    }
  };

  // Toggle filing type expansion within a card
  const toggleFilingTypeExpand = (viewerId, filingType) => {
    const key = `${viewerId}-${filingType}`;
    setExpandedFilingTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculate days old
  const calculateDaysOld = (timestamp) => {
    const created = new Date(timestamp);
    const now = new Date();
    // Use date-only comparison to avoid timezone issues
    const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate());
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffTime = todayDate - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    // Ensure we don't return negative days
    return Math.max(0, diffDays);
  };

  // Get age category (matching dashboard)
  const getAgeCategory = (timestamp) => {
    const days = calculateDaysOld(timestamp);
    if (days < 10) return 'New';
    if (days <= 30) return 'Recent';
    return 'Older';
  };

  // Open dossier - returns viewer_id on success for immediate use
  const openDossier = async (ticker, docTypes = null) => {
    try {
      setGenerating(true);
      setSelectedTicker(ticker);
      setShowDossierModal(true);
      
      // Build preferences from selected document types
      const prefs = docTypes || selectedDocTypes;
      const documentPrefs = {
        filing_types: [],
        include_statements: prefs.includeStatements || false
      };
      
      if (prefs.include10K) documentPrefs.filing_types.push('10-K');
      if (prefs.include10Q) documentPrefs.filing_types.push('10-Q');
      if (prefs.include8K) documentPrefs.filing_types.push('8-K');
      if (prefs.include20F) documentPrefs.filing_types.push('20-F');
      if (prefs.include6K) documentPrefs.filing_types.push('6-K');
      if (prefs.includeProxy) documentPrefs.filing_types.push('Proxy');
      
      // Start generation as a background job (immune to proxy timeouts on large tickers)
      const startRes = await fetch(`${API_BASE_URL}/api/generate-web-viewer-async/${ticker}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...documentPrefs, user_id: user?.id })
      });
      if (!startRes.ok) throw new Error('Failed to start dossier generation');
      const { job_id } = await startRes.json();
      
      // Poll until done (up to ~6 minutes)
      let data = null;
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const statusRes = await fetch(`${API_BASE_URL}/api/generate-web-viewer-status/${job_id}`);
        if (!statusRes.ok) continue;
        const statusData = await statusRes.json();
        if (statusData.status === 'done') { data = statusData; break; }
        if (statusData.status === 'failed') throw new Error(statusData.error || 'Generation failed');
      }
      if (!data) throw new Error('Generation timed out');
      
      const viewerUrl = `${API_BASE_URL}${data.shareable_url}?embed=true`;
      setDossierUrl(viewerUrl);
      
      // Return viewer_id for callers that need to update state after creation
      return data.viewer_id;
    } catch (error) {
      console.error('Error opening dossier:', error);
      showNotification?.('Failed to load dossier', 'error');
      setShowDossierModal(false);
      return null;
    } finally {
      setGenerating(false);
    }
  };

  // Open full viewer with all documents, optionally scrolling to a specific section
  const openDossierById = async (viewerId, ticker, sectionHash = null) => {
    const newUrl = `${API_BASE_URL}/api/viewer/${viewerId}?embed=true`;
    const isSameViewer = dossierUrl === newUrl && showDossierModal;
    
    setSelectedTicker(ticker || viewerId);
    setShowDossierModal(true);
    setViewerReady(false);
    setTargetSectionHash(sectionHash);
    
    if (isSameViewer && sectionHash && iframeRef.current) {
      // Same viewer already open - just scroll to the new section
      console.log('Same viewer open, scrolling directly to:', sectionHash);
      iframeRef.current.contentWindow.postMessage({
        type: 'scrollToSection',
        sectionId: sectionHash
      }, '*');
    } else {
      // Different viewer or not open - load the URL
      console.log('Opening full viewer for section:', sectionHash);
      setDossierUrl(newUrl);
    }
  };

  // Send scroll command when viewer is ready and we have a target section
  useEffect(() => {
    if (viewerReady && targetSectionHash && iframeRef.current) {
      console.log('Viewer ready, sending scroll message for section:', targetSectionHash);
      iframeRef.current.contentWindow.postMessage({
        type: 'scrollToSection',
        sectionId: targetSectionHash
      }, '*');
    }
  }, [viewerReady, targetSectionHash]);

  // Handle iframe load - send multiple scroll attempts to ensure delivery
  const handleIframeLoad = () => {
    if (targetSectionHash && iframeRef.current) {
      console.log('Iframe loaded, target section:', targetSectionHash);
      // Send multiple attempts with increasing delays to ensure the message is received
      const sendScrollMessage = () => {
        if (iframeRef.current && iframeRef.current.contentWindow && targetSectionHash) {
          console.log('Sending scroll message for:', targetSectionHash);
          iframeRef.current.contentWindow.postMessage({
            type: 'scrollToSection',
            sectionId: targetSectionHash
          }, '*');
        }
      };
      // Retry at 100ms, 300ms, 600ms, 1000ms - skip immediate since DOM may not be ready
      setTimeout(sendScrollMessage, 100);
      setTimeout(sendScrollMessage, 300);
      setTimeout(sendScrollMessage, 600);
      setTimeout(sendScrollMessage, 1000);
    }
  };

  const closeDossierModal = () => {
    setShowDossierModal(false);
    setDossierUrl(null);
    setSelectedTicker(null);
    setTargetSectionHash(null);
    setViewerReady(false);
  };

  // Create new file
  const createNewDossier = async () => {
    if (!newTicker.trim()) {
      showNotification?.('Please enter a ticker symbol', 'error');
      return;
    }
    const ticker = newTicker.trim().toUpperCase();
    setShowCreateModal(false);
    setNewTicker('');
    setTickerSuggestions([]);
    
    // Trigger blue dot notification for new file BEFORE async operation
    console.log('🔵 FilesTab: Triggering file notification, onFileCreated exists:', !!onFileCreated);
    if (onFileCreated) {
      console.log('🔵 FilesTab: Calling onFileCreated callback NOW');
      onFileCreated();
      console.log('🔵 FilesTab: onFileCreated callback completed');
    } else {
      console.warn('🔵 FilesTab: WARNING - onFileCreated prop is not defined!');
    }
    
    const viewerId = await openDossier(ticker);
    // Refresh dossiers list after creation completes
    await fetchDossiers();
    // Expand the newly created card using the returned viewer_id
    if (viewerId) {
      setExpandedCards(prev => ({ ...prev, [viewerId]: true }));
      fetchDocumentsForDossier(viewerId, ticker);
    }
  };

  // Create file from "no file" modal prompt
  const createFileFromPrompt = async () => {
    if (!noFileTickerInfo?.ticker) return;
    
    const ticker = noFileTickerInfo.ticker;
    setShowNoFileModal(false);
    setNoFileTickerInfo(null);
    
    // Trigger notification
    if (onFileCreated) {
      onFileCreated();
    }
    
    const viewerId = await openDossier(ticker);
    await fetchDossiers();
    
    if (viewerId) {
      setExpandedCards(prev => ({ ...prev, [viewerId]: true }));
      fetchDocumentsForDossier(viewerId, ticker);
      // Scroll to the new card
      setTimeout(() => {
        const cardElement = document.querySelector(`[data-testid="dossier-card-${ticker}"]`);
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
    }
  };

  // Add documents to an existing viewer without opening the viewer modal
  const addDocumentsToExisting = async (viewerId, ticker) => {
    if (!viewerId || !ticker) {
      showNotification?.('Missing viewer information', 'error');
      return;
    }
    
    // Prevent double submission
    if (addingDocuments) {
      console.log('📁 Already adding documents, ignoring duplicate call');
      return;
    }
    
    setShowCreateModal(false);
    setAddingDocuments(true);
    setNotificationShown(false); // Reset notification guard for this operation
    
    // Store the viewerId we're updating for scrolling later
    const targetViewerId = viewerId;
    
    try {
      // First, get the existing filing types from the current viewer
      const existingFilingTypes = new Set();
      const existingDocs = cardDocuments[viewerId];
      if (existingDocs?.filings) {
        existingDocs.filings.forEach(f => {
          if (f.type) existingFilingTypes.add(f.type);
        });
      }
      
      console.log('📁 Existing filing types:', Array.from(existingFilingTypes));
      
      // Build the combined list of filing types (existing + new)
      const allFilingTypes = new Set(existingFilingTypes);
      
      if (selectedDocTypes.include10K) allFilingTypes.add('10-K');
      if (selectedDocTypes.include10Q) allFilingTypes.add('10-Q');
      if (selectedDocTypes.include8K) allFilingTypes.add('8-K');
      if (selectedDocTypes.include20F) allFilingTypes.add('20-F');
      if (selectedDocTypes.include6K) allFilingTypes.add('6-K');
      if (selectedDocTypes.includeProxy) allFilingTypes.add('Proxy');
      
      const combinedFilingTypes = Array.from(allFilingTypes);
      
      console.log('📁 Combined filing types:', combinedFilingTypes);
      
      // Build preferences with force_regenerate to bypass the 1-hour cache
      const documentPrefs = {
        filing_types: combinedFilingTypes,
        include_statements: selectedDocTypes.includeStatements || false,
        viewer_id: viewerId,
        user_id: user?.id,
        force_regenerate: true // Force regeneration to bypass cache
      };
      
      console.log('📁 Adding documents to existing viewer:', viewerId, 'with combined types:', combinedFilingTypes);
      
      const response = await fetch(`${API_BASE_URL}/api/generate-web-viewer-async/${ticker}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(documentPrefs)
      });
      
      if (!response.ok) throw new Error('Failed to add documents');
      
      const { job_id } = await response.json();
      let data = null;
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const statusRes = await fetch(`${API_BASE_URL}/api/generate-web-viewer-status/${job_id}`);
        if (!statusRes.ok) continue;
        const statusData = await statusRes.json();
        if (statusData.status === 'done') { data = statusData; break; }
        if (statusData.status === 'failed') throw new Error(statusData.error || 'Failed to add documents');
      }
      if (!data) throw new Error('Adding documents timed out');
      console.log('📁 Documents added successfully:', data);
      
      // Refresh the documents list for this specific card
      await fetchDocumentsForDossier(viewerId, ticker);
      
      // Also refresh the main dossiers list to get updated metadata
      await fetchDossiers();
      
      // Show notification only once
      if (!notificationShown) {
        setNotificationShown(true);
        showNotification?.('Documents added successfully', 'success');
      }
      
      // Auto-scroll to the updated card after a brief delay to let the DOM update
      setTimeout(() => {
        const cardRef = cardRefs.current[targetViewerId];
        if (cardRef) {
          cardRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('📁 Scrolled to updated card:', targetViewerId);
        }
      }, 150);
      
    } catch (error) {
      console.error('Error adding documents:', error);
      if (!notificationShown) {
        setNotificationShown(true);
        showNotification?.('Failed to add documents', 'error');
      }
    } finally {
      setAddingDocuments(false);
      setAddingToViewerId(null);
      setNewTicker('');
      setTickerSuggestions([]);
    }
  };

  // Group and sort dossiers
  const groupedDossiers = React.useMemo(() => {
    const tickerMap = new Map();
    dossiers.forEach(d => {
      const key = d.ticker || d.viewer_id;
      if (!tickerMap.has(key) || new Date(d.created_at) > new Date(tickerMap.get(key).created_at)) {
        tickerMap.set(key, d);
      }
    });
    return Array.from(tickerMap.values());
  }, [dossiers]);

  // Sort dossiers based on sortBy, with today's scheduled items always at top
  const sortedDossiers = React.useMemo(() => {
    let sorted = [...groupedDossiers];
    
    // Helper to check if a dossier is scheduled for today
    const isScheduledToday = (dossier) => {
      return todayScheduled.some(item => 
        item.ticker?.toUpperCase() === dossier.ticker?.toUpperCase()
      );
    };
    
    switch (sortBy) {
      case 'fresh':
        sorted = sorted.filter(d => calculateDaysOld(d.created_at) < 10);
        break;
      case 'warm':
        sorted = sorted.filter(d => {
          const days = calculateDaysOld(d.created_at);
          return days >= 10 && days <= 30;
        });
        break;
      case 'stale':
        sorted = sorted.filter(d => calculateDaysOld(d.created_at) > 30);
        break;
      default:
        // newest - sort by date
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    // Always prioritize today's scheduled dossiers to the top
    sorted.sort((a, b) => {
      const aScheduled = isScheduledToday(a);
      const bScheduled = isScheduledToday(b);
      
      if (aScheduled && !bScheduled) return -1;
      if (!aScheduled && bScheduled) return 1;
      return 0; // Keep existing order for same category
    });
    
    return sorted;
  }, [groupedDossiers, sortBy, todayScheduled]);

  // Share
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Tickler Files',
          text: 'Check out my SEC filings research',
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showNotification?.('Link copied to clipboard', 'success');
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  // Share a specific file - use parent's share modal
  const shareDossier = (e, viewerId, ticker) => {
    e.stopPropagation();
    if (onShareFile) {
      onShareFile(ticker || viewerId, viewerId);
    }
  };

  // Delete a file - confirm first, no success banner after
  const deleteDossier = async (e, viewerId, ticker) => {
    e.stopPropagation();
    
    if (showConfirmModal) {
      showConfirmModal({
        title: 'Delete File',
        message: `Are you sure you want to delete "${ticker || viewerId}"? This cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700'
      }).then(async (confirmed) => {
        if (confirmed) {
          try {
            const res = await fetch(`${API_BASE_URL}/api/viewer/${viewerId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              fetchDossiers();
            } else {
              showNotification?.('Failed to delete file', 'error');
            }
          } catch (error) {
            console.error('Error deleting file:', error);
            showNotification?.('Failed to delete file', 'error');
          }
        }
        closeConfirmModal?.();
      });
    }
  };

  // Format date like other cards
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderCompaniesView = () => {
    // Filter dossiers based on filterView only (no cross-panel filtering - we highlight instead)
    const filteredDossiers = filterView === 'scheduled' 
      ? sortedDossiers.filter(dossier => 
          todayScheduled.some(scheduled => 
            scheduled.ticker?.toUpperCase() === dossier.ticker?.toUpperCase()
          )
        )
      : sortedDossiers;

    return (
    <div className={`p-3 sm:p-4 space-y-3 ${isDarkMode ? 'bg-slate-900' : ''}`}>
      {filteredDossiers.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border-2 border-dashed ${
          isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'
        }`}>
          <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-lg font-medium mb-2">
            {filterView === 'scheduled' ? 'No scheduled files for today' : 'No files yet'}
          </p>
          <p className="text-sm mb-4">
            {filterView === 'scheduled' 
              ? 'Schedule research on ideas to see them here'
              : <>Search <code className={`${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} px-2 py-1 rounded`}>file:AAPL</code> or create new</>
            }
          </p>
          {filterView !== 'scheduled' && (
            <button
              onClick={() => {
                // Reset state when opening modal
                setNewTicker('');
                setTickerSuggestions([]);
                setSecAvailability({ loading: false, hasFilings: null, hasForeignFilings: null, message: '' });
                setSelectedDocTypes({
                  include10K: true,
                  include10Q: true,
                  include8K: false,
                  include20F: false,
                  include6K: false,
                  includeProxy: false,
                  includeStatements: false
                });
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-all"
            >
              Create File
            </button>
          )}
        </div>
      ) : (
        filteredDossiers.map((dossier, index) => {
          const ticker = dossier.ticker;
          const ageCategory = getAgeCategory(dossier.created_at);
          const daysOld = calculateDaysOld(dossier.created_at);
          const isExpanded = expandedCards[dossier.viewer_id];
          
          // Border color based on age (matching idea cards)
          let cardBorderColor = 'border-gray-200';
          let ageBadgeColor = 'bg-gray-100 text-gray-800';
          
          if (ageCategory === 'New') {
            cardBorderColor = 'border-green-500';
            ageBadgeColor = 'bg-green-100 text-green-800';
          } else if (ageCategory === 'Recent') {
            cardBorderColor = 'border-orange-500';
            ageBadgeColor = 'bg-orange-100 text-orange-800';
          } else {
            cardBorderColor = 'border-red-500';
            ageBadgeColor = 'bg-red-100 text-red-800';
          }
          
          // Check if this dossier is scheduled for today
          const isScheduledToday = todayScheduled.some(item => 
            item.ticker?.toUpperCase() === ticker?.toUpperCase()
          );
          
          // Check if this ticker is highlighted from cross-panel linking
          const isHighlighted = highlightedTicker && ticker?.toUpperCase() === highlightedTicker;
          
          // Status bar color based on age (matching idea cards design)
          let statusBarColor = 'bg-zinc-600';
          if (ageCategory === 'New') statusBarColor = 'bg-emerald-500';
          else if (ageCategory === 'Recent') statusBarColor = 'bg-amber-500';
          else statusBarColor = 'bg-rose-500';
          
          return (
            <div 
              key={dossier.viewer_id || index}
              ref={(el) => { cardRefs.current[dossier.viewer_id] = el; }}
              className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${
                isDarkMode 
                  ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' 
                  : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
              } ${isHighlighted ? 'workspace-highlight' : ''}`}
              data-testid={`dossier-card-${ticker || dossier.viewer_id}`}
              data-file-ticker={ticker?.toUpperCase()}
            >
              {/* Status Bar - Left Edge (age-based color) */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
              {/* Card content - clickable to expand */}
              <button
                onClick={() => toggleCardExpand(dossier.viewer_id, ticker)}
                className={`w-full px-4 pl-5 py-3 ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'} transition-colors text-left`}
              >
                <div className="space-y-0.5">
                  {/* Row 1: File icon + Ticker + Today pill | sections count (far right) */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className={`font-mono font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        ${ticker || dossier.viewer_id}
                      </span>
                      {isScheduledToday && (
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Today</span>
                      )}
                    </div>
                    <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {dossier.sections_count || cardDocuments[dossier.viewer_id]?.sections_count || dossier.document_count || 0} sections
                    </span>
                  </div>
                  
                  {/* Row 2: Company name (indented under ticker) | Arrow (far right) */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ml-7 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {dossier.company_name || ''}
                    </span>
                    <svg className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
              </button>
              
              {/* Action buttons - Only show when expanded */}
              {isExpanded && (
              <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 py-2 justify-center border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-100 bg-slate-50'}`}>
                {/* Delete button */}
                <button
                  onClick={(e) => deleteDossier(e, dossier.viewer_id, ticker)}
                  className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                  title="Delete file"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                  <span className="text-[10px] sm:text-xs">Delete</span>
                </button>
                {/* Share button */}
                <button
                  onClick={(e) => shareDossier(e, dossier.viewer_id, ticker)}
                  className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                  title="Share file"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                  </svg>
                  <span className="text-[10px] sm:text-xs">Share</span>
                </button>
                {/* Open button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDossierById(dossier.viewer_id, ticker);
                  }}
                  className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                  title="Open file"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-[10px] sm:text-xs">Open</span>
                </button>
              </div>
              )}
              
              {/* Expanded content - Documents list only */}
              {isExpanded && (
                <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                  {/* Documents list */}
                  <div className={`px-4 py-3 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-slate-50/50'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      DOCUMENTS INCLUDED {cardDocuments[dossier.viewer_id]?.sections_count ? `(${cardDocuments[dossier.viewer_id].sections_count} sections)` : ''}
                    </p>
                    <div className="space-y-2">
                      {/* Dynamic filing types from API - each expandable */}
                      {(cardDocuments[dossier.viewer_id]?.filings || []).map((filing, idx) => {
                        // Color scheme for each filing type
                        const colorSchemes = {
                          '10-K': { bg: 'bg-blue-900/50', bgLight: 'bg-blue-100', text: 'text-blue-400', textLight: 'text-blue-600' },
                          '10-Q': { bg: 'bg-green-900/50', bgLight: 'bg-green-100', text: 'text-green-400', textLight: 'text-green-600' },
                          '8-K': { bg: 'bg-orange-900/50', bgLight: 'bg-orange-100', text: 'text-orange-400', textLight: 'text-orange-600' },
                          '20-F': { bg: 'bg-cyan-900/50', bgLight: 'bg-cyan-100', text: 'text-cyan-400', textLight: 'text-cyan-600' },
                          '6-K': { bg: 'bg-teal-900/50', bgLight: 'bg-teal-100', text: 'text-teal-400', textLight: 'text-teal-600' },
                          'Proxy': { bg: 'bg-pink-900/50', bgLight: 'bg-pink-100', text: 'text-pink-400', textLight: 'text-pink-600' },
                          'Financial': { bg: 'bg-purple-900/50', bgLight: 'bg-purple-100', text: 'text-purple-400', textLight: 'text-purple-600' }
                        };
                        // Match filing type to color scheme
                        let colorKey = filing.type;
                        if (filing.type?.toLowerCase().includes('proxy')) colorKey = 'Proxy';
                        if (filing.type?.toLowerCase().includes('financial') || filing.type?.toLowerCase().includes('statement')) colorKey = 'Financial';
                        const colors = colorSchemes[colorKey] || colorSchemes['10-K'];
                        const isFilingTypeExpanded = expandedFilingTypes[`${dossier.viewer_id}-${filing.type}`];
                        const itemCount = filing.count || filing.items?.length || 0;
                        
                        return (
                          <div key={filing.type || idx} className={`rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-white'} border ${isDarkMode ? 'border-slate-600' : 'border-slate-200'} overflow-hidden`}>
                            {/* Filing type header - clickable to expand */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFilingTypeExpand(dossier.viewer_id, filing.type);
                              }}
                              className={`w-full flex items-center gap-3 p-2 ${isDarkMode ? 'hover:bg-slate-600/50' : 'hover:bg-slate-50'} transition-colors`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? colors.bg : colors.bgLight}`}>
                                <svg className={`w-4 h-4 ${isDarkMode ? colors.text : colors.textLight}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div className="flex-1 text-left">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                  {filing.name}
                                  {itemCount > 0 && (
                                    <span className={`ml-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      ({itemCount})
                                    </span>
                                  )}
                                </p>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{filing.description}</p>
                              </div>
                              <svg 
                                className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} transition-transform duration-200 ${isFilingTypeExpanded ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Individual filings dropdown */}
                            {isFilingTypeExpanded && filing.items && filing.items.length > 0 && (
                              <div className={`border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                {filing.items.map((item, itemIdx) => (
                                  <div
                                    key={item.id || itemIdx}
                                    className={`w-full flex items-center gap-3 px-4 py-2 ${
                                      itemIdx < filing.items.length - 1 ? `border-b ${isDarkMode ? 'border-slate-600/50' : 'border-slate-100'}` : ''
                                    }`}
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDarkMode ? colors.text : colors.textLight}`}></div>
                                    <div className="flex-1 min-w-0">
                                      <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                        {item.label || `${filing.type} - ${item.date}`}
                                      </span>
                                      {item.title && (
                                        <p className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          {item.title}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Loading state */}
                      {!cardDocuments[dossier.viewer_id] && (
                        <div className={`flex items-center justify-center p-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading documents...
                        </div>
                      )}
                    </div>
                    
                    {/* Add Documents button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Track which viewer we're adding documents to
                        setAddingToViewerId(dossier.viewer_id);
                        setNewTicker(ticker);
                        checkSecAvailability(ticker);
                        setShowCreateModal(true);
                      }}
                      disabled={addingDocuments}
                      className={`mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed transition-all ${
                        addingDocuments
                          ? 'opacity-50 cursor-not-allowed'
                          : isDarkMode
                            ? 'border-slate-600 text-slate-400 hover:border-orange-500 hover:text-orange-400'
                            : 'border-slate-300 text-slate-500 hover:border-orange-500 hover:text-orange-500'
                      }`}
                    >
                      {addingDocuments && addingToViewerId === dossier.viewer_id ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm font-medium">Adding...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-sm font-medium">Add Documents</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
  };

  const renderReceivedView = () => (
    <div className={`text-center py-16 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
      <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
      <p className="text-lg font-medium mb-2">No received files</p>
      <p className="text-sm">Scheduled files will appear here</p>
    </div>
  );

  // Create File Modal - matching document selection modal
  const renderCreateModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => { setShowCreateModal(false); setAddingToViewerId(null); }}>
      <div 
        className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {addingToViewerId ? 'Add Documents' : 'Create New File'}
            </h2>
            <button 
              onClick={() => { setShowCreateModal(false); setAddingToViewerId(null); }}
              className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
            >✕</button>
          </div>
          
          <div className="space-y-6">
            {/* Ticker Input with Autocomplete */}
            <div>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                {addingToViewerId ? 'Adding to' : 'Select Ticker'}
              </h3>
              <div className="relative">
                <input
                  type="text"
                  value={newTicker}
                  onChange={(e) => {
                    if (addingToViewerId) return; // Don't allow changing ticker when adding to existing
                    const val = e.target.value.toUpperCase();
                    setNewTicker(val);
                    searchTickers(val);
                    // Reset SEC availability when typing
                    setSecAvailability({ loading: false, hasFilings: null, hasForeignFilings: null, message: '' });
                    // Reset checkboxes to default US company settings when typing
                    setSelectedDocTypes({
                      include10K: true,
                      include10Q: true,
                      include8K: false,
                      include20F: false,
                      include6K: false,
                      includeProxy: false,
                      includeStatements: false
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTicker.trim()) {
                      setTickerSuggestions([]);
                      checkSecAvailability(newTicker.trim());
                    }
                  }}
                  placeholder={addingToViewerId ? newTicker : "Search ticker or company name (e.g., AAPL, Apple)"}
                  disabled={!!addingToViewerId}
                  className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:border-orange-500 ${
                    addingToViewerId 
                      ? 'bg-slate-100 cursor-not-allowed font-bold text-lg'
                      : isDarkMode 
                        ? 'bg-[#1a2235] border-slate-700 text-white placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'
                  } ${isDarkMode && addingToViewerId ? 'bg-slate-700' : ''}`}
                  autoFocus={!addingToViewerId}
                />
                {/* Ticker Suggestions with Company Names */}
                {tickerSuggestions.length > 0 && (
                  <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg border z-10 max-h-64 overflow-y-auto ${
                    isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'
                  }`}>
                    {tickerSuggestions.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setNewTicker(item.ticker);
                          setTickerSuggestions([]);
                          // Check SEC availability immediately when selecting
                          checkSecAvailability(item.ticker);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-orange-50 flex items-center justify-between border-b last:border-b-0 ${
                          isDarkMode ? 'text-white hover:bg-slate-700 border-slate-700' : 'text-slate-800 border-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`font-bold text-orange-500 min-w-[60px]`}>{item.ticker}</span>
                          <span className={`text-sm truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{item.company}</span>
                        </div>
                        {item.source && item.source !== 'search' && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.source === 'idea' 
                              ? 'bg-blue-100 text-blue-700' 
                              : item.source === 'watchlist'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.source === 'idea' ? 'Idea' : item.source === 'watchlist' ? 'Collection' : 'Poll'}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Document Selection - matching existing modal */}
            <div>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                Select Documents
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Choose which SEC filings to include in the file.
              </p>
              
              {/* SEC Availability Status */}
              {newTicker && (
                <div className={`mb-4 p-3 rounded-lg ${
                  secAvailability.loading 
                    ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100')
                    : secAvailability.hasFilings
                      ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200')
                      : secAvailability.hasForeignFilings
                        ? (isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200')
                        : secAvailability.hasFilings === false
                          ? (isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200')
                          : (isDarkMode ? 'bg-slate-700' : 'bg-gray-100')
                }`}>
                  {secAvailability.loading ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Checking SEC filings for {newTicker}...</span>
                    </div>
                  ) : secAvailability.hasFilings ? (
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                        <strong>US Company</strong> — SEC filings available
                      </span>
                    </div>
                  ) : secAvailability.hasForeignFilings ? (
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        <strong>Foreign Company with US Filings</strong> — 20-F/6-K available
                      </span>
                    </div>
                  ) : secAvailability.hasFilings === false ? (
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                        <strong>Not an SEC Filer</strong> — No SEC filings found for {newTicker}
                      </span>
                    </div>
                  ) : (
                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Select a ticker to check SEC filing availability
                    </span>
                  )}
                </div>
              )}
              
              <div className="space-y-3">
                {/* 10-K */}
                <label className={`flex items-center justify-between ${!secAvailability.hasFilings && secAvailability.hasFilings !== null ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>10-K Annual Reports</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Comprehensive annual business disclosure</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.include10K}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, include10K: e.target.checked})}
                    disabled={!secAvailability.hasFilings && secAvailability.hasFilings !== null}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>

                {/* 10-Q */}
                <label className={`flex items-center justify-between ${!secAvailability.hasFilings && secAvailability.hasFilings !== null ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-green-900/50' : 'bg-green-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>10-Q Quarterly Reports</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Quarterly financial reports</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.include10Q}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, include10Q: e.target.checked})}
                    disabled={!secAvailability.hasFilings && secAvailability.hasFilings !== null}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>

                {/* 8-K */}
                <label className={`flex items-center justify-between ${!secAvailability.hasFilings && secAvailability.hasFilings !== null ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-orange-900/50' : 'bg-orange-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>8-K Current Reports</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Material events and changes</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.include8K}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, include8K: e.target.checked})}
                    disabled={!secAvailability.hasFilings && secAvailability.hasFilings !== null}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>

                {/* Proxy Statements */}
                <label className={`flex items-center justify-between ${!secAvailability.hasFilings && secAvailability.hasFilings !== null ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-pink-900/50' : 'bg-pink-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Proxy Statements</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Executive compensation & governance</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.includeProxy}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, includeProxy: e.target.checked})}
                    disabled={!secAvailability.hasFilings && secAvailability.hasFilings !== null}
                    className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>

                {/* Divider for Foreign Filings */}
                <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>FOREIGN ISSUER FILINGS</p>
                </div>

                {/* 20-F */}
                <label className={`flex items-center justify-between ${!secAvailability.hasForeignFilings && secAvailability.hasForeignFilings !== null ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>20-F Annual Reports</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Foreign company annual reports</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.include20F}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, include20F: e.target.checked})}
                    disabled={!secAvailability.hasForeignFilings && secAvailability.hasForeignFilings !== null}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>

                {/* 6-K */}
                <label className={`flex items-center justify-between ${!secAvailability.hasForeignFilings && secAvailability.hasForeignFilings !== null ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-indigo-900/50' : 'bg-indigo-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>6-K Reports</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Foreign company interim reports</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.include6K}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, include6K: e.target.checked})}
                    disabled={!secAvailability.hasForeignFilings && secAvailability.hasForeignFilings !== null}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>

                {/* Divider for Additional Data */}
                <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>ADDITIONAL DATA</p>
                </div>

                {/* Financial Statements */}
                <label className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 ${isDarkMode ? 'bg-emerald-900/50' : 'bg-emerald-100'} rounded-lg mr-3`}>
                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Financial Statements</span>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Income Statement, Balance Sheet, Cash Flow</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedDocTypes.includeStatements}
                    onChange={(e) => setSelectedDocTypes({...selectedDocTypes, includeStatements: e.target.checked})}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setAddingToViewerId(null);
                  setNewTicker('');
                  setTickerSuggestions([]);
                }}
                className={`flex-1 px-4 py-3 rounded-lg border transition-all ${
                  isDarkMode 
                    ? 'border-slate-600 text-slate-300 hover:bg-slate-800' 
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (addingToViewerId) {
                    // Adding documents to existing viewer
                    addDocumentsToExisting(addingToViewerId, newTicker.trim().toUpperCase());
                  } else {
                    // Creating new file
                    createNewDossier();
                  }
                }}
                disabled={!newTicker.trim() || (secAvailability.hasFilings === false && secAvailability.hasForeignFilings === false)}
                className={`flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-all ${
                  !newTicker.trim() || (secAvailability.hasFilings === false && secAvailability.hasForeignFilings === false) 
                    ? 'opacity-50 cursor-not-allowed' 
                    : ''
                }`}
              >
                {secAvailability.hasFilings === false && secAvailability.hasForeignFilings === false 
                  ? 'No SEC Filings Available' 
                  : addingToViewerId ? 'Add Documents' : 'Generate File'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // File viewer modal
  const renderDossierModal = () => createPortal(
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        zIndex: 99999,
        overflow: 'hidden'
      }}
      onClick={closeDossierModal}
    >
      <div 
        style={{ width: '100%', height: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {generating ? (
          <div className="flex items-center justify-center h-full bg-white">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Loading {selectedTicker} file...</p>
            </div>
          </div>
        ) : dossierUrl ? (
          <iframe
            ref={iframeRef}
            src={dossierUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block'
            }}
            title={`${selectedTicker} SEC Filings`}
            allow="fullscreen"
            onLoad={handleIframeLoad}
          />
        ) : null}
      </div>
    </div>,
    document.body
  );

  // Render "No File Created" modal - shows when navigating to a file that doesn't exist
  const renderNoFileModal = () => {
    if (!noFileTickerInfo) return null;
    
    // Format scheduled date for display
    const formatScheduledDate = (dateStr) => {
      if (!dateStr) return null;
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    };
    
    const scheduledDateDisplay = formatScheduledDate(noFileTickerInfo.scheduledDate);
    const isScheduledForFuture = noFileTickerInfo.scheduledDate && noFileTickerInfo.scheduledDate > new Date().toISOString().split('T')[0];
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" 
        onClick={() => { setShowNoFileModal(false); setNoFileTickerInfo(null); }}
      >
        <div 
          className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-xl max-w-md w-full shadow-2xl`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                <svg className={`w-6 h-6 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div>
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  No File Created Yet
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  ${noFileTickerInfo.ticker}{noFileTickerInfo.companyName ? ` - ${noFileTickerInfo.companyName}` : ''}
                </p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              A research file for <strong>${noFileTickerInfo.ticker}</strong> hasn't been created yet.
            </p>
            
            {/* Scheduled info if available */}
            {noFileTickerInfo.scheduledDate && (
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <div className="flex items-center gap-2">
                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <span className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    {isScheduledForFuture 
                      ? `Scheduled for ${scheduledDateDisplay}` 
                      : `Was scheduled for ${scheduledDateDisplay}`
                    }
                  </span>
                </div>
              </div>
            )}
            
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {isScheduledForFuture 
                ? "You can create the file now or wait until the scheduled date when it may be auto-generated."
                : "Would you like to create this file now?"
              }
            </p>
          </div>
          
          {/* Actions */}
          <div className={`p-4 flex gap-3 border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-100 bg-slate-50'}`}>
            <button
              onClick={() => { setShowNoFileModal(false); setNoFileTickerInfo(null); }}
              className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                isDarkMode 
                  ? 'text-slate-300 hover:bg-slate-700' 
                  : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              {isScheduledForFuture ? 'Wait for Schedule' : 'Cancel'}
            </button>
            <button
              onClick={createFileFromPrompt}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
              Create File Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={isWorkspaceMode ? 'h-full flex flex-col overflow-hidden' : ''}>
      <Card className={`border-0 shadow-lg overflow-hidden ${isWorkspaceMode ? 'h-full flex flex-col' : ''} ${isDarkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-white to-slate-50'}`}>
        <CardContent className={`p-0 ${isWorkspaceMode ? 'flex-1 flex flex-col overflow-hidden' : ''}`}>
          {/* Header */}
          <div className="p-3 bg-slate-800 text-white rounded-t-xl flex-shrink-0">
            <div className="flex items-center justify-between">
              {/* Left: Tab toggle - always visible */}
              <div className="flex items-center bg-black/20 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('companies')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                    activeTab === 'companies'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  My Files
                </button>
                <button
                  onClick={() => setActiveTab('received')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                    activeTab === 'received'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  Received
                </button>
              </div>
              
              {/* Center: Shortcut hint - desktop only, hide in workspace mode */}
              {!isWorkspaceMode && (
              <button 
                onClick={() => onOpenGlobalSearch?.()}
                className="hidden sm:flex items-center text-white/50 text-sm"
                title="View keyboard shortcuts"
              >
                <span>Press </span>
                <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                <span> for shortcuts</span>
              </button>
              )}
              
              {/* Right: Action buttons - icon-only in workspace mode */}
              <div className="flex items-center gap-1">
                {/* Search - opens global search */}
                <button
                  onClick={() => onOpenGlobalSearch?.()}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg"
                  title="Search file:TICKER"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
                
                {/* Sort dropdown - matching other tabs */}
                <div className="relative">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFilterDropdown(false); // Close the other dropdown
                      setShowSortDropdown(!showSortDropdown);
                    }}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg"
                    title="Sort"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                  </button>
                  
                  {showSortDropdown && (
                    <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'}`}>
                      <div className="py-1">
                        <button
                          onClick={() => { setSortBy('newest'); setShowSortDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-slate-400 mr-2"></div>
                            All (newest first)
                          </div>
                        </button>
                        <button
                          onClick={() => { setSortBy('fresh'); setShowSortDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                            New (&lt; 10 days)
                          </div>
                        </button>
                        <button
                          onClick={() => { setSortBy('warm'); setShowSortDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                            Recent (10-30 days)
                          </div>
                        </button>
                        <button
                          onClick={() => { setSortBy('stale'); setShowSortDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                            Older (30+ days)
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Filter - Funnel icon to switch between all files and scheduled */}
                <div className="relative">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSortDropdown(false); // Close the other dropdown
                      setShowFilterDropdown(!showFilterDropdown);
                    }}
                    className={`p-2 border rounded-lg ${filterView === 'scheduled' ? 'text-orange-500 border-orange-500 bg-orange-500/10' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`}
                    title="Filter files"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </button>
                  {showFilterDropdown && (
                    <div className={`absolute right-0 top-full mt-2 w-48 rounded-lg shadow-xl border py-1 z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterView('all');
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} ${
                          filterView === 'all' ? (isDarkMode ? 'text-blue-300 bg-blue-900/50' : 'text-blue-700 bg-blue-50') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                        </svg>
                        All Files
                        {filterView === 'all' && (
                          <svg className={`w-4 h-4 ml-auto ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterView('scheduled');
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} ${
                          filterView === 'scheduled' ? (isDarkMode ? 'text-blue-300 bg-blue-900/50' : 'text-blue-700 bg-blue-50') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Scheduled Today ({todayScheduled.length})
                        {filterView === 'scheduled' && (
                          <svg className={`w-4 h-4 ml-auto ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Create New File (Orange) */}
                <button
                  onClick={() => {
                    // Reset state when opening modal
                    setNewTicker('');
                    setTickerSuggestions([]);
                    setSecAvailability({ loading: false, hasFilings: null, hasForeignFilings: null, message: '' });
                    setSelectedDocTypes({
                      include10K: true,
                      include10Q: true,
                      include8K: false,
                      include20F: false,
                      include6K: false,
                      includeProxy: false,
                      includeStatements: false
                    });
                    setShowCreateModal(true);
                  }}
                  className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                  title="Create New File"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`${isWorkspaceMode ? 'flex-1 overflow-y-auto' : ''} ${isDarkMode ? 'bg-slate-900' : ''}`}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : activeTab === 'companies' ? (
              renderCompaniesView()
            ) : (
              renderReceivedView()
            )}
          </div>
        </CardContent>
      </Card>

      {showCreateModal && renderCreateModal()}
      {showDossierModal && renderDossierModal()}
      {showNoFileModal && renderNoFileModal()}
    </div>
  );
};

export default FilesTab;
